import { getClientFormInfo } from "@/entities/client/lib/getClientFormInfo";
import { Button, Dialog, Skeleton, Stack } from "@fxc/ui-kit";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormContainer } from "react-hook-form-mui";
import { toast } from "react-toastify";
import {
  ClientInput,
  ClientsDocument,
  ProposalNavigationPositionType,
  useCreateClientMutation,
  useDefaultInfoQuery,
} from "../../../../generated/apollo";
import { FormButtons } from "../../../components/FormButtons";
import { BootstrapDialogTitle } from "../../../components/layout/BootstrapDialogTitle";
import { ClientEditableFields } from "./ClientEditableFields";

export function getClientDefaultValues() {
  return {
    keycloakGroupName: "",
    usersLimit: 10,
    applicationTitle: "Sales Enablement Platform",
    applicationBaseUrl: "",
    applicationLogoUrl: "",
    applicationBrandColor: "#FFFFFF", // TODO mui theme of colors
    loginTheme: "",
    proposalNavigationPosition: ProposalNavigationPositionType.Horizontal,
    clientPermissions: [],
    entityNamePlural: "Proposals",
    entityNameSingular: "Proposal",
    insightPlaceholderSingular: "Insight",
    insightPlaceholderPlural: "Insights",
    clientFeatures: [],
    bannerEnabled: false,
    bannerHeader: "",
    bannerText: "",
  } satisfies ClientInput;
}

export function CreateClient() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const [createClient] = useCreateClientMutation({
    awaitRefetchQueries: true,
    refetchQueries: [ClientsDocument],
  });
  const defaultValues = useMemo(getClientDefaultValues, []);
  const { data } = useDefaultInfoQuery();
  const default_app_url = data?.defaultInfo?.default_app_url;
  const clientFormInfo = useMemo(
    () => (default_app_url ? getClientFormInfo({ default_app_url }) : null),
    [default_app_url],
  );

  const form = useForm({
    defaultValues,
    resolver: clientFormInfo?.resolver as undefined, // just for ts
  });

  return (
    <>
      <Button
        data-qa="create-client"
        sx={{
          m: "auto",
          mt: 2,
          display: "block",
        }}
        onClick={() => {
          setOpen(true);
        }}
        variant="contained"
      >
        Create client
      </Button>
      <Dialog
        slotProps={{
          paper: {
            sx: {
              width: 0.3,
            },
          },
        }}
        data-qa="create-client-dialog"
        open={open}
      >
        <FormContainer
          onSuccess={(client) => {
            void toast
              .promise(
                createClient({ variables: { client } }).then(() =>
                  form.reset(defaultValues),
                ),
                {
                  pending: `Creating client "${client.keycloakGroupName}"...`,
                  success: `Client "${client.keycloakGroupName}" successfully created`,
                },
              )
              .then(close);
          }}
          formContext={form}
          FormProps={{
            autoComplete: "off",
            style: {
              textAlign: "center",
            },
          }}
        >
          <BootstrapDialogTitle onClose={close}>
            Create client
          </BootstrapDialogTitle>

          <Stack sx={{ m: 2, gap: 2 }}>
            {data ? (
              <ClientEditableFields
                defaultInfo={data.defaultInfo}
                form={form}
              />
            ) : (
              <Skeleton />
            )}
          </Stack>

          <FormButtons sx={{ mx: "auto", mb: 2 }} />
        </FormContainer>
      </Dialog>
    </>
  );
}
